import React from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// For /api/ paths we deliberately use a RELATIVE url: the browser will resolve
// it against the current origin (zottezomerronse.be on prod, preview host on dev).
// This avoids stale REACT_APP_BACKEND_URL values baked into a previous build
// from breaking images / brochure downloads on a redeploy.
const resolveSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return url; // relative -> same-origin
    return url;
};

/**
 * OrganizerLogo — shows either an uploaded logo image (via settings) or
 * a Bungee text fallback.
 *  - variant 'stad'   → Stad Ronse, hoofdorganisator (larger, primary)
 *  - variant 'bright' → Bright Events, uitvoering (smaller, secondary; rendered in
 *                       a small black card with cream border to match the gold-on-black wordmark)
 * size: 'sm' | 'md' | 'lg'
 * onDark: when true, Stad Ronse SVG/PNG is wrapped in a cream card so coloured marks
 *         remain legible on dark backgrounds. Bright Events black-card already works
 *         on both light and dark contexts.
 */
export default function OrganizerLogo({ url, variant = 'stad', size = 'md', onDark = false, label = null, testId = null }) {
    const src = resolveSrc(url);
    const altText = variant === 'stad' ? 'Logo Stad Ronse' : 'Logo Bright Events';
    const resolvedTestId = testId || `org-logo-${variant}`;

    // --- IMAGE PATH --------------------------------------------------------
    if (src) {
        if (variant === 'bright') {
            // Gold-on-black wordmark in a black card with cream border.
            // Inner heights tuned so the thin gold lettering stays legible.
            const innerH = {
                sm: 'h-12 sm:h-14',   // 48 / 56 px  — Hero, Footer
                md: 'h-14 sm:h-16',   // 56 / 64 px  — About
                lg: 'h-16 sm:h-20',   // 64 / 80 px
            }[size];
            const cardPad = 'px-3.5 py-2.5';
            return (
                <span
                    data-testid={`${resolvedTestId}-card`}
                    aria-label={altText}
                    className={`inline-flex items-center justify-center bg-black border-2 border-cream/90 rounded-2xl ${cardPad} shadow-md`}
                >
                    <img
                        src={src}
                        alt={altText}
                        loading="lazy"
                        decoding="async"
                        data-testid={resolvedTestId}
                        className={`${innerH} w-auto object-contain block`}
                    />
                </span>
            );
        }

        // Stad Ronse: SVG wordmark (aspect ~3.26:1) — sized so it stays dominant
        // versus Bright Events at every breakpoint.
        const stadH = {
            sm: 'h-8 sm:h-10',                // 32 / 40 px
            md: 'h-12 sm:h-16 md:h-20',       // 48 / 64 / 80 px — Hero, Footer
            lg: 'h-20 sm:h-28 md:h-36',       // 80 / 112 / 144 px — About
        }[size];
        const img = (
            <img
                src={src}
                alt={altText}
                loading="lazy"
                decoding="async"
                data-testid={resolvedTestId}
                className={`${stadH} w-auto object-contain block`}
            />
        );
        if (onDark) {
            return (
                <span
                    className="inline-flex items-center justify-center bg-cream rounded-lg px-3 py-2 shadow-[0_2px_0_rgba(0,0,0,0.18)]"
                    aria-label={altText}
                >
                    {img}
                </span>
            );
        }
        return img;
    }

    // --- TEXT FALLBACK -----------------------------------------------------
    const textSize = {
        sm: 'text-xs sm:text-sm',
        md: 'text-base sm:text-xl',
        lg: 'text-2xl sm:text-4xl md:text-5xl',
    }[size];

    if (variant === 'stad') {
        return (
            <div
                data-testid={resolvedTestId}
                className={`font-display ${textSize} inline-flex items-center gap-2 leading-none ${onDark ? 'text-cream' : 'text-[var(--green-dark)]'}`}
                style={{ letterSpacing: '-0.02em' }}
                aria-label={altText}
            >
                <span
                    className={`inline-flex items-center justify-center px-2.5 py-1 rounded-md ${onDark ? 'bg-cream text-[var(--green-dark)]' : 'bg-[var(--green)] text-cream'}`}
                >
                    STAD
                </span>
                <span>RONSE</span>
            </div>
        );
    }

    // bright events text fallback (used only if logo URL is empty)
    return (
        <div
            data-testid={resolvedTestId}
            className={`font-display ${textSize} inline-flex items-center gap-1.5 leading-none ${onDark ? 'text-cream/90' : 'text-[var(--green-dark)]/80'}`}
            style={{ letterSpacing: '-0.02em' }}
            aria-label={altText}
        >
            <span>BRIGHT</span>
            <span className="opacity-70">EVENTS</span>
        </div>
    );
}
